<template>
  <template v-if="summary">
    <div>
      <h2>
        {{ t('personalStatistics') }}
      </h2>
      <p class="text-sm text-gray-500">
        {{ currentQuarterLabel }}
      </p>
    </div>
    <div class="flex flex-col gap-4">
      <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:gap-8">
        <div class="item">
          <div>
            <div class="flex items-center gap-1">
              <div class="text-md font-semibold">
                {{ t('bookingsCreated') }}
              </div>
              <Information class="text-gray-500">
                <template #trigger>
                  <InformationCircleIcon class="h-5 w-5" />
                </template>
                <template #modal>
                  {{ t('bookingsExclusionInfo') }}
                </template>
              </Information>
            </div>
            <div class="text-xs text-gray-500">
              {{ t('count') }}
            </div>
          </div>
          <span class="text-2xl font-semibold">
            {{ formatCount(summary.bookingsCreated) }}
          </span>
        </div>
        <div class="item">
          <div>
            <div class="flex items-center gap-1">
              <div class="text-md font-semibold">
                {{ t('bookingsRevenue') }}
              </div>
              <DashboardEmployeeStatValue
                :value="summary.bookingsRevenue"
                :prepend-info="[t('bookingsExclusionInfo'), t('bookingsRevenueCompletedInfo')]"
              />
            </div>
            <div class="text-xs text-gray-500">
              {{ t('sumNet') }}
            </div>
          </div>
          <DashboardCurrencyAmount :number="summary.bookingsRevenue" />
        </div>
      </div>
      <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:gap-8">
        <div class="item">
          <div>
            <div class="text-md font-semibold">
              {{ t('businessCustomersCreated') }}
            </div>
            <div class="text-xs text-gray-500">
              {{ t('count') }}
            </div>
          </div>
          <span class="text-2xl font-semibold">
            {{ formatCount(summary.businessCustomersCreated) }}
          </span>
        </div>
      </div>
    </div>
  </template>
  <template v-else>
    <Spinner />
  </template>
</template>

<script lang="ts" setup>
import Information from '@/components/Information.vue';
import { InformationCircleIcon } from '@heroicons/vue/24/outline';
import { DateTime } from 'luxon';
import { useEmployeeDashboardSummary } from '@/queries/use-employee-dashboard-summary';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import DashboardCurrencyAmount from '@/views/dashboard/components/DashboardAgentProvisionCurrency.vue';
import DashboardEmployeeStatValue from '@/views/dashboard/components/DashboardEmployeeStatValue.vue';

const { t, n } = useI18n();
const { data: summary } = useEmployeeDashboardSummary();

const currentQuarterLabel = computed(() => {
  const now = DateTime.now().setZone('Europe/Berlin');
  return t('currentQuarter', { quarter: now.quarter, year: now.year });
});

const formatCount = (value: number) =>
  n(value, {
    maximumFractionDigits: 0,
  });
</script>

<style scoped>
.item {
  @apply flex flex-row items-center justify-between gap-2 rounded-md bg-white px-4 md:bg-transparent md:px-0;
}
</style>

<i18n lang="json">
{
  "en": {
    "bookingsCreated": "Bookings created",
    "bookingsExclusionInfo": "Excluding agency, broker and insurance bookings.",
    "bookingsRevenueCompletedInfo": "Only completed bookings.",
    "bookingsRevenue": "Booking revenue",
    "businessCustomersCreated": "Business customers created",
    "personalStatistics": "Personal statistics",
    "currentQuarter": "Q{quarter} {year}",
    "count": "count",
    "sumNet": "sum (excl. VAT)"
  },
  "de": {
    "bookingsCreated": "Erstellte Buchungen",
    "bookingsExclusionInfo": "Ohne Vermittler-, Broker- und Versicherungsbuchungen.",
    "bookingsRevenueCompletedInfo": "Nur abgeschlossene Buchungen.",
    "bookingsRevenue": "Umsatz Buchungen",
    "businessCustomersCreated": "Angelegte Businesskunden",
    "personalStatistics": "Persönliche Statistik",
    "currentQuarter": "Q{quarter} {year}",
    "count": "Anzahl",
    "sumNet": "Summe (netto)"
  },
  "es": {
    "bookingsCreated": "Reservas creadas",
    "bookingsExclusionInfo": "Sin reservas de agencias, brokers ni seguros.",
    "bookingsRevenueCompletedInfo": "Solo reservas completadas.",
    "bookingsRevenue": "Ingresos por reservas",
    "businessCustomersCreated": "Clientes empresa creados",
    "personalStatistics": "Estadísticas personales",
    "currentQuarter": "T{quarter} {year}",
    "count": "cantidad",
    "sumNet": "suma (sin IVA)"
  }
}
</i18n>
